// The shared motion vocabulary for translations. Intent is "settle, don't
// flash": colour is one continuous ramp (grey -> blue -> green, orange/red as
// off-ramps), so a change reads as colour flowing forward. Nothing loops except
// the waiting breath.

// -- Durations (ms) ---------------------------------------------------------
export const translationsMotion = {
  durations: {
    instant: 0, // reduced-motion target: everything collapses to this
    fast: 120, // hover/press, dot nudge, popover close
    base: 200, // tint bleed, chip cross-fade, panel chrome, popover open
    settle: 320, // checkmark draw + green completion settle
    ping: 520, // one-shot kickoff ring
    wash: 600, // one-shot row acknowledgement wash
    breath: 1400, // one queued/generating pulse
  },
  easings: {
    standard: 'cubic-bezier(0.2, 0, 0, 1)', // decelerate into rest
    emphasised: 'cubic-bezier(0.3, 0, 0, 1)', // longer tail: "arrives with weight"
    breathe: 'cubic-bezier(0.4, 0, 0.6, 1)', // symmetric, for the breath
  },
} as const;

const { durations, easings } = translationsMotion;

// Dots breathe in unison. Index is threaded through so a desync could be
// restored here (e.g. `(index % 5) * -320`) without touching callers.
export function breathDelayMs(_index: number): number {
  return 0;
}

// -- Ready-made CSS strings -------------------------------------------------

// The tint bleed: bg/colour/border interpolate along the ramp over `base`.
export const TINT_TRANSITION = `background-color ${durations.base}ms ${easings.standard}, color ${durations.base}ms ${easings.standard}, border-color ${durations.base}ms ${easings.standard}, box-shadow ${durations.base}ms ${easings.standard}`;

export const DOT_TINT_TRANSITION = `background-color ${durations.base}ms ${easings.standard}`;

export const TOOLBAR_DOT_TINT_TRANSITION = `background-color ${durations.fast}ms ${easings.standard}`;

// The trough the breath dips to at 50%; deep enough to read without blinking.
const BREATH_MIN_OPACITY = 0.45;

// Defined once so the panel dot and toolbar dot can't drift. `BREATH_KEYFRAMES`
// is the matching `@keyframes` block to spread into the element's `sx`.
const BREATH_KEYFRAME_NAME = 'translationDotBreath';
export const BREATH_ANIMATION = `${BREATH_KEYFRAME_NAME} ${durations.breath}ms ${easings.breathe} infinite`;
export const BREATH_KEYFRAMES = {
  [`@keyframes ${BREATH_KEYFRAME_NAME}`]: {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: BREATH_MIN_OPACITY },
  },
} as const;

// One-shot halo pinging out of the status dot when translation begins. The
// `@keyframes translationKickoffRing` lives in the dot's `sx`.
export const KICKOFF_RING_ANIMATION = `translationKickoffRing ${durations.ping}ms ${easings.standard} forwards`;

// Coverage-bar segments glide as counts change (green advancing into blue reads
// as colour flowing forward). Interpolation needs every segment mounted: a
// segment that unmounts at zero pops instead of gliding.
export const BAR_SEGMENT_TRANSITION = `flex-grow ${durations.base}ms ${easings.standard}`;
